import React, { memo, useEffect, useCallback, useMemo, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useLocation } from 'react-router-dom';
import { ACTIVITIES, CONVERSATIONS } from 'constants/Entities';
import { QuickFilters } from '@web/web5';
import { EntityFiltersActions, ConfigActions, FuzzyActions, ServerListActions } from 'actions';
import { FuzzyList } from 'utils/fuzzy';
import { getBackendBoolean } from 'utils/fm';
import { getLiteral } from 'utils/getLiteral';
import { mappedActivityTypeIds, activityTypesWithoutSubfilters } from 'models/ActivityModel';
import classnames from 'classnames';
import { inConversations } from '../../utils/conversations';

import './styles.scss';

const mapStateToProps = (state) => {
    const filters = state.entityFilters?.[ACTIVITIES.entity]?.filters || {};
    return {
        filters,
        activityTypeValues: filters?.activityType?.value || [],
    };
};

const mapDispatchToProps = (dispatch, ownProps) => {
    let name = ownProps.entity ? ownProps.entity.entity : '';
    const setConfigWeb = bindActionCreators(ConfigActions, dispatch).setConfigWeb;

    return {
        initFilters: bindActionCreators(EntityFiltersActions, dispatch).initFilters,
        toggleFilters: bindActionCreators(EntityFiltersActions, dispatch).toggleFilters,
        changeFilter: bindActionCreators(EntityFiltersActions, dispatch).changeFilter,
        changeAdvancedFilter: bindActionCreators(EntityFiltersActions, dispatch)
            .changeAdvancedFilter,
        deleteMultipleFilters: bindActionCreators(EntityFiltersActions, dispatch)
            .deleteMultipleFilters,
        clearFilters: bindActionCreators(EntityFiltersActions, dispatch).clearFilters,
        saveCache: (config) => setConfigWeb(`${name}_filters`, config),
        setConfigWeb,
        getFuzzy: bindActionCreators(FuzzyActions, dispatch).getFuzzy,
        getList: bindActionCreators(ServerListActions, dispatch).getList,
        removeView: bindActionCreators(EntityFiltersActions, dispatch).removeView,
        createView: bindActionCreators(EntityFiltersActions, dispatch).createView,
        setFilterDragging: bindActionCreators(EntityFiltersActions, dispatch).setFilterDragging,
    };
};

const FiltersRow = memo(
    ({ initFilters, getFuzzy, getList, filters, changeFilter, activityTypeValues }) => {
        const [schema, setSchema] = useState([]);
        const [finalSchema, setFinalSchema] = useState([]);
        const [mappedSchema, setMappedSchema] = useState({});
        const isFirstRender = useRef(true);
        const oldActivityTypeValues = useRef(activityTypeValues);
        const location = useLocation();

        const loadOptions = useCallback(
            (field) => () => {
                if (!field?.inputAttrs?.list) return Promise.resolve([]);
                return new Promise((resolve, reject) => {
                    getList(field.inputAttrs.list)
                        .then((options) => {
                            let newOptions = options;
                            if (field.beforeRenderValueList) {
                                newOptions = field.beforeRenderValueList(newOptions);
                            }
                            if (field.groupFunction) {
                                newOptions = field.groupFunction(options);
                            }
                            resolve(newOptions);
                        })
                        .catch(reject);
                });
            },
            [getList],
        );

        const loadFuzzyOptions = useCallback(
            (field) => {
                return (text) => {
                    return new Promise((resolve) => {
                        if (!field.inputAttrs) return resolve([]);
                        if (cachedOptions.current[text]) return resolve(cachedOptions[text]);
                        clearTimeout(debounce.current);
                        debounce.current = setTimeout(() => {
                            fetchOptions({ text, ...field.inputAttrs })
                                .then((options) => {
                                    let newOptions = options;
                                    if (field.beforeRenderValueList) {
                                        newOptions = field.beforeRenderValueList(newOptions);
                                    }
                                    if (field.groupFunction) {
                                        newOptions = field.groupFunction(options);
                                    }
                                    cachedOptions.current[text] = newOptions;
                                    resolve(newOptions);
                                })
                                .catch(console.error);
                        }, 500);
                    });
                };
            },
            [fetchOptions],
        );

        const isFuzzy = useCallback((list, field) => {
            if (!list) return false;
            return (
                FuzzyList.includes(list.toLowerCase()) ||
                getBackendBoolean(field.fuzzySearchValueList)
            );
        }, []);

        const getFieldFormatted = useCallback(
            (field) => {
                const newField = { ...field };
                if (!newField.inputProps) newField.inputProps = {};
                if (field.hint) newField.inputProps.placeholder = field.hint;
                if (field?.inputAttrs?.actions) {
                    newField.inputProps.actions = field.inputAttrs.actions;
                    newField.inputProps.forceMenuIsOpen = true;
                }
                switch (field.dataType) {
                    case 'bool':
                        newField.inputProps.label = getLiteral('label_filters_boolean');
                        break;
                    case 'probability':
                        newField.inputProps = {
                            min: 0,
                            max: 100,
                        };
                        break;
                    case 'singleValueList':
                    case 'relatedValueList':
                    case 'multipleValueList':
                        if (field?.generateOptions) {
                            newField.inputProps.options = field.generateOptions() || [];
                        } else if (
                            field?.inputAttrs?.list &&
                            isFuzzy(field?.inputAttrs?.list, field)
                        ) {
                            newField.inputProps.isFuzzy = true;
                            newField.inputProps.isMulti = newField.inputAttrs.hasOwnProperty(
                                'isMulti',
                            )
                                ? newField.inputAttrs.isMulti
                                : true;
                            newField.inputProps.loadOptions = loadFuzzyOptions(field);
                            newField.inputProps.defaultOptions = true;
                            newField.inputProps.defaultSearch =
                                field.inputAttrs?.defaultSearch || '';
                        } else {
                            newField.inputProps.isMulti = true;
                            if (field?.inputAttrs?.list) {
                                newField.inputProps.loadOptions = loadOptions(field);
                            }
                        }

                        if (!newField.inputProps.placeholder) {
                            newField.inputProps.placeholder = getLiteral('label_selectone');
                        }

                        if (field?.inputAttrs?.optionAllLabel) {
                            newField.inputProps.optionAllLabel = field.inputAttrs.optionAllLabel;
                        }

                        if (field.inputAttrs?.overrides) {
                            if (newField.inputProps.overrides) {
                                newField.inputProps.overrides = {
                                    ...newField.inputProps.overrides,
                                    ...field.inputAttrs.overrides,
                                    'react-select': {
                                        ...newField.inputProps.overrides['react-select'],
                                        ...(field.inputAttrs.overrides?.['react-select'] || {}),
                                    },
                                };
                            } else {
                                newField.inputProps.overrides = {
                                    ...field.inputAttrs.overrides,
                                };
                            }
                        }

                        if (field.inputAttrs?.className) {
                            newField.inputProps.className = field.inputAttrs.className;
                        }

                        if (field.inputAttrs?.getClassName) {
                            newField.inputProps.className = field.inputAttrs.getClassName();
                        }

                        break;
                    case 'text':
                        if (!newField?.inputAttrs?.placeholder) {
                            newField.inputProps.placeholder = getLiteral('placeholder_search');
                        }
                }

                return {
                    ...newField,
                };
            },
            [isFuzzy, loadFuzzyOptions, loadOptions],
        );

        const getFieldsFormatted = useCallback(
            (fields) => {
                const formattedField = getFieldFormatted(fields);
                return [formattedField];
            },
            [getFieldFormatted],
        );

        const getFinalSchema = useCallback(
            (schema, mappedSchema) => {
                if (!schema || schema.length === 0) return [];
                const newSchema = schema.reduce((arr, current) => {
                    if (!mappedSchema[current.id]) return arr;
                    arr.push({
                        label: mappedSchema[current.id].description,
                        id: mappedSchema[current.id].id,
                        fields: [...getFieldsFormatted(mappedSchema[current.id])],
                    });
                    return arr;
                }, []);
                setFinalSchema(newSchema);
            },
            [getFieldsFormatted],
        );

        const getMappedSchema = useCallback((schema) => {
            return schema.reduce((obj, current) => {
                if (!obj.schemaByFieldId) obj.schemaByFieldId = {};
                if (!obj.schemaByFieldConfiguration) obj.schemaByFieldConfiguration = {};
                obj.schemaByFieldId[current.id] = current;
                if (current.fieldConfiguration) {
                    obj.schemaByFieldConfiguration[current.fieldConfiguration] = current;
                }
                return obj;
            }, {});
        }, []);

        const init = useCallback(() => {
            initFilters(ACTIVITIES).then(([schema]) => {
                const subFilterSchema = schema.filter((current) => current.isSubFilter);
                const { schemaByFieldId } = getMappedSchema(subFilterSchema);
                setMappedSchema(schemaByFieldId);
                setSchema(subFilterSchema);
                getFinalSchema(subFilterSchema, schemaByFieldId);
            });
        }, [getMappedSchema, initFilters, getFinalSchema]);

        useEffect(() => {
            if (isFirstRender.current) {
                init();
                isFirstRender.current = false;
            }
        }, [init]);

        useEffect(() => {
            if (oldActivityTypeValues?.current?.length !== activityTypeValues?.length) {
                getFinalSchema(schema, mappedSchema);
                oldActivityTypeValues.current = activityTypeValues;
            }
        }, [activityTypeValues, getFinalSchema, schema, mappedSchema]);

        const fetchOptions = useCallback(
            ({ text, list, field, defaultSearch }) => {
                return getFuzzy({ field, list, text, feature: ACTIVITIES.entity });
            },
            [getFuzzy],
        );

        const onChangeFilter = useCallback(
            (filter, value, info) => {
                let completeValues = null;
                let newValue = null;

                if (value && Array.isArray(value)) {
                    completeValues = value.map((current) => {
                        if (!newValue) newValue = [];
                        newValue.push(current.value || current);
                        return {
                            value: current.value || current,
                            label: current.label,
                            idParent: current.idParent || current.idparent,
                        };
                    });
                } else {
                    newValue = value?.value || value;
                    completeValues = value;
                }

                if (filter.customFormatValueToSave) {
                    newValue = filter.customFormatValueToSave(newValue);
                }

                if (filter.customFormatCompleteValuesToSave) {
                    completeValues = filter.customFormatCompleteValuesToSave(
                        completeValues,
                        filter,
                    );
                }

                const inputAttrs = filter.inputAttrs || null;

                if (
                    (!newValue ||
                        (inputAttrs?.minSelection && inputAttrs?.minSelection > newValue.length)) &&
                    inputAttrs?.backupSelection
                ) {
                    newValue = inputAttrs.backupSelection?.value || null;
                    completeValues = inputAttrs.backupSelection?.completeValues || null;
                }

                const relatedEntity = inConversations(location) ? CONVERSATIONS : null;

                changeFilter(
                    ACTIVITIES,
                    filter,
                    newValue,
                    undefined,
                    completeValues,
                    null,
                    info,
                    false,
                    relatedEntity,
                );
            },
            [changeFilter, location],
        );

        const values = useMemo(() => {
            if (!filters || filters.length === 0) return {};

            const newValues = Object.keys(filters).reduce((obj, key) => {
                let value = filters[key].completeValues || filters[key].value;
                obj[key] = value;
                return obj;
            }, {});

            return newValues;
        }, [filters]);

        const activeFilters = useMemo(() => {
            if (!filters?.activityType?.value?.length) return [];
            const activityTypeValues = filters.activityType.value;
            const newActiveFilters = activityTypeValues.reduce((arr, current) => {
                if (mappedActivityTypeIds[current] && !activityTypesWithoutSubfilters[current])
                    arr.push(mappedActivityTypeIds[current]);
                return arr;
            }, []);
            return newActiveFilters;
        }, [filters]);

        const className = classnames('activities-filters-row', {
            'activities-filters-row__collapsed': !activeFilters?.length,
        });

        return (
            <div className={className}>
                {activeFilters?.length > 0 && (
                    <QuickFilters
                        className="activities-filters-subfilters"
                        useQuickView={false}
                        filters={activeFilters}
                        schema={finalSchema}
                        onChange={onChangeFilter}
                        values={values}
                        chipSize="small"
                        isSubfilter={true}
                    />
                )}
            </div>
        );
    },
);

export default connect(mapStateToProps, mapDispatchToProps)(FiltersRow);
